import axios from 'axios';

import {
  getApiUrl,
  getQuery,
  returnHeaders,
  viewResErrors,
} from '@shared/api/baseQueryTypes';

import type { AppAxiosResponse } from '@shared/api/baseQueryTypes';

import { appendReportBranchIdsToQueryParts } from '../lib/buildReportBranchQueryParams';
import {
  finalizeReportQueryBodyForGroupBy,
  serializeReportQueryRequest,
} from '../lib/buildReportGroupParam';
import {
  expandCompositeSelectedFields,
  isReportCompositeFieldPath,
} from '../lib/reportEntityCompositeFields';

import type {
  ReportEntityListItem,
  ReportEntityMetadata,
  ReportQueryPageable,
  ReportQueryRequest,
  ReportQueryResponse,
} from '../types/reportApiTypes';

function sanitizeReportQueryBody(body: ReportQueryRequest): ReportQueryRequest {
  const selectedFields = expandCompositeSelectedFields(body.selectedFields ?? []).filter(
    (field) => field.fieldName && !isReportCompositeFieldPath(field.fieldName),
  );
  return finalizeReportQueryBodyForGroupBy({ ...body, selectedFields });
}

/** POST query/export: сырой JSON, чтобы having Long ушёл как N.0, не Integer. */
async function postReportQueryBody<T>(
  url: string,
  body: ReportQueryRequest,
): Promise<AppAxiosResponse<T>> {
  const requestUrl = `${getApiUrl()}${url}`;
  try {
    const res = await axios.post<T>(requestUrl, serializeReportQueryRequest(body), {
      headers: {
        ...returnHeaders(),
        'Content-Type': 'application/json',
      },
      transformRequest: [(data) => data],
    });
    return res as AppAxiosResponse<T>;
  } catch (e) {
    return viewResErrors<T>(e as Parameters<typeof viewResErrors>[0]);
  }
}

/** Сигнал для UI: обрыв ответа (ERR_HTTP2_PROTOCOL_ERROR и т.п.), не ошибка бизнес-логики. */
export const REPORT_QUERY_TRANSPORT_ERROR = 'REPORT_QUERY_TRANSPORT_ERROR';

const REPORT_QUERY_ATTEMPTS = 3;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Нет HTTP-статуса — тело JSON не дошло до axios (типично ERR_HTTP2_PROTOCOL_ERROR в DevTools). */
function isLikelyTransportFailure(res: AppAxiosResponse<unknown>): boolean {
  if (!res.isError) return false;
  if (res.status != null && res.status >= 400) return false;
  return res.status === 0 || res.data == null;
}

function unwrap<T>(res: {
  data: T | null;
  isError?: boolean;
  message?: string;
  detail?: string;
}): T {
  if (res.isError || res.data == null) {
    throw new Error(res.message || res.detail || 'reports request failed');
  }
  return res.data;
}

function buildQueryUrl(entityName: string, pageable?: ReportQueryPageable): string {
  const encoded = encodeURIComponent(entityName);
  const base = `api/v1/reports/${encoded}/query`;
  const queryParts: string[] = [];
  if (pageable) {
    queryParts.push(`page=${pageable.page}`, `size=${pageable.size}`);
    for (const sort of pageable.sort ?? []) {
      if (sort) {
        queryParts.push(`sort=${sort}`);
      }
    }
    appendReportBranchIdsToQueryParts(queryParts, pageable.branchIds);
  }
  return queryParts.length ? `${base}?${queryParts.join('&')}` : base;
}

export async function fetchReportEntities(): Promise<ReportEntityListItem[]> {
  const res = await getQuery<ReportEntityListItem[]>({
    url: 'api/v1/reports/entities',
  });
  const data = unwrap(res);
  return Array.isArray(data) ? data : [];
}

export async function fetchReportEntityMetadata(entityName: string): Promise<ReportEntityMetadata> {
  const encoded = encodeURIComponent(entityName);
  const res = await getQuery<ReportEntityMetadata>({
    url: `api/v1/reports/${encoded}/metadata`,
  });
  return unwrap(res);
}

export async function executeReportQuery(
  entityName: string,
  body: ReportQueryRequest,
  pageable?: ReportQueryPageable,
): Promise<ReportQueryResponse> {
  const url = buildQueryUrl(entityName, pageable);
  let lastRes: AppAxiosResponse<ReportQueryResponse> | null = null;

  for (let attempt = 0; attempt < REPORT_QUERY_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await delay(400 * attempt);
    }

    const res = await postReportQueryBody<ReportQueryResponse>(
      url,
      sanitizeReportQueryBody(body),
    );

    if (!res.isError && res.data != null) {
      return res.data;
    }

    lastRes = res;
    if (!isLikelyTransportFailure(res) || attempt >= REPORT_QUERY_ATTEMPTS - 1) {
      break;
    }
  }

  if (lastRes && isLikelyTransportFailure(lastRes)) {
    throw new Error(REPORT_QUERY_TRANSPORT_ERROR);
  }

  throw new Error(lastRes?.detail || lastRes?.message || 'reports query failed');
}

export type ReportExportFormat = 'CSV' | 'XLS' | 'PDF';

export async function exportReport(
  entityName: string,
  format: ReportExportFormat,
  fileName: string,
  body: ReportQueryRequest,
  branchIds?: number[],
): Promise<Blob> {
  const encoded = encodeURIComponent(entityName);
  const queryParts = [`format=${encodeURIComponent(format)}`];
  if (fileName.trim()) {
    queryParts.push(`fileName=${encodeURIComponent(fileName.trim())}`);
  }
  appendReportBranchIdsToQueryParts(queryParts, branchIds);
  const url = `${getApiUrl()}api/v1/reports/${encoded}/export?${queryParts.join('&')}`;

  const res = await axios.post(url, serializeReportQueryRequest(sanitizeReportQueryBody(body)), {
    headers: {
      ...returnHeaders(),
      'Content-Type': 'application/json',
    },
    transformRequest: [(data) => data],
    responseType: 'blob',
  });

  if (res.status >= 400) {
    throw new Error('report export failed');
  }
  return res.data as Blob;
}
